import { useState, useEffect } from 'react'
import {
    AreaChart, Area, LineChart, Line, BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts'
import { Droplet, Thermometer, Zap, AlertTriangle, RefreshCw, Calendar } from 'lucide-react'
import { generateSoilData, generateHistoricalData, CROP_RANGES, CROP_CONFIG } from '../../utils/mockDataGenerator'
import './SoilDashboard.css'

function SoilDashboard({ crop }) {
    const [soilData, setSoilData] = useState([])
    const [historicalData, setHistoricalData] = useState([])
    const [selectedZone, setSelectedZone] = useState(null)
    const [granularity, setGranularity] = useState('daily')

    useEffect(() => {
        // Generate data when crop changes
        setSoilData(generateSoilData(crop))
        setHistoricalData(generateHistoricalData(crop, 30))
        setSelectedZone(null)
    }, [crop])

    const ranges = CROP_RANGES[crop] || CROP_RANGES['Tomate']
    const cropConfig = CROP_CONFIG[crop] || CROP_CONFIG['Tomate']

    const getStatus = (value, range) => {
        if (value < range.min) return { status: 'low', label: 'Bajo', color: '#F59E0B' }
        if (value > range.max) return { status: 'high', label: 'Alto', color: '#EF4444' }
        return { status: 'optimal', label: 'Óptimo', color: '#10B981' }
    }

    const refreshData = () => {
        setSoilData(generateSoilData(crop))
        setHistoricalData(generateHistoricalData(crop, 30))
    }

    return (
        <div className="soil-dashboard">
            <div className="dashboard-header">
                <div>
                    <h2>Monitoreo de Suelo - {crop}</h2>
                    <p>Sensores IoT en tiempo real para N, P, K, pH, Humedad y más</p>
                </div>
                <div className="header-actions">
                    <select
                        value={granularity}
                        onChange={(e) => setGranularity(e.target.value)}
                        className="granularity-select"
                    >
                        <option value="hourly">Por Hora</option>
                        <option value="daily">Diario</option>
                        <option value="weekly">Semanal</option>
                    </select>
                    <button className="btn btn-secondary" onClick={refreshData}>
                        <RefreshCw size={18} />
                        Actualizar
                    </button>
                </div>
            </div>

            {/* Zone Cards */}
            <div className="zones-grid">
                {soilData.map((zone) => (
                    <div
                        key={zone.id}
                        className={`zone-card glass-card ${selectedZone === zone.id ? 'selected' : ''}`}
                        onClick={() => setSelectedZone(zone.id === selectedZone ? null : zone.id)}
                    >
                        <div className="zone-header">
                            <h3>{zone.name}</h3>
                            <span className="zone-status">
                                {getStatus(zone.nitrogen, ranges.nitrogen).status !== 'optimal' && (
                                    <AlertTriangle size={16} color="#F59E0B" />
                                )}
                            </span>
                        </div>
                        <div className="zone-metrics">
                            <div className="metric-item">
                                <span className="metric-label">pH</span>
                                <span
                                    className="metric-value"
                                    style={{ color: getStatus(zone.ph, ranges.ph).color }}
                                >
                                    {zone.ph}
                                </span>
                            </div>
                            <div className="metric-item">
                                <span className="metric-label">Humedad</span>
                                <span
                                    className="metric-value"
                                    style={{ color: getStatus(zone.humidity, ranges.humidity).color }}
                                >
                                    {zone.humidity}%
                                </span>
                            </div>
                            <div className="metric-item">
                                <span className="metric-label">N</span>
                                <span
                                    className="metric-value"
                                    style={{ color: getStatus(zone.nitrogen, ranges.nitrogen).color }}
                                >
                                    {zone.nitrogen}
                                </span>
                            </div>
                            <div className="metric-item">
                                <span className="metric-label">P</span>
                                <span
                                    className="metric-value"
                                    style={{ color: getStatus(zone.phosphorus, ranges.phosphorus).color }}
                                >
                                    {zone.phosphorus}
                                </span>
                            </div>
                            <div className="metric-item">
                                <span className="metric-label">K</span>
                                <span
                                    className="metric-value"
                                    style={{ color: getStatus(zone.potassium, ranges.potassium).color }}
                                >
                                    {zone.potassium}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Charts Section */}
            <div className="charts-section">
                <div className="chart-card glass-card">
                    <h3><Calendar size={18} /> Variables del Suelo (Últimos 30 días)</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={historicalData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis dataKey="dateDisplay" stroke="#64748B" fontSize={11} />
                            <YAxis stroke="#64748B" fontSize={12} />
                            <Tooltip
                                contentStyle={{
                                    background: '#1E293B',
                                    border: '1px solid #334155',
                                    borderRadius: '8px'
                                }}
                            />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="ph"
                                stroke="#10B981"
                                strokeWidth={2}
                                dot={false}
                                name="pH"
                            />
                            <Line
                                type="monotone"
                                dataKey="humidity"
                                stroke="#0EA5E9"
                                strokeWidth={2}
                                dot={false}
                                name="Humedad %"
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                <div className="chart-card glass-card">
                    <h3>Nutrientes NPK</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={soilData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
                            <YAxis stroke="#64748B" fontSize={12} />
                            <Tooltip
                                contentStyle={{
                                    background: '#1E293B',
                                    border: '1px solid #334155',
                                    borderRadius: '8px'
                                }}
                            />
                            <Legend />
                            <Bar dataKey="nitrogen" fill="#10B981" name="N (mg/kg)" />
                            <Bar dataKey="phosphorus" fill="#0EA5E9" name="P (mg/kg)" />
                            <Bar dataKey="potassium" fill="#F59E0B" name="K (mg/kg)" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Optimal Ranges Reference */}
            <div className="ranges-card glass-card">
                <h3>Rangos Óptimos para {crop}</h3>
                <div className="ranges-grid">
                    <div className="range-item">
                        <span className="range-label">pH</span>
                        <span className="range-value">{ranges.ph.min} - {ranges.ph.max}</span>
                    </div>
                    <div className="range-item">
                        <span className="range-label">Humedad</span>
                        <span className="range-value">{ranges.humidity.min}% - {ranges.humidity.max}%</span>
                    </div>
                    <div className="range-item">
                        <span className="range-label">Nitrógeno</span>
                        <span className="range-value">{ranges.nitrogen.min} - {ranges.nitrogen.max} mg/kg</span>
                    </div>
                    <div className="range-item">
                        <span className="range-label">Fósforo</span>
                        <span className="range-value">{ranges.phosphorus.min} - {ranges.phosphorus.max} mg/kg</span>
                    </div>
                    <div className="range-item">
                        <span className="range-label">Potasio</span>
                        <span className="range-value">{ranges.potassium.min} - {ranges.potassium.max} mg/kg</span>
                    </div>
                    <div className="range-item">
                        <span className="range-label">Temperatura</span>
                        <span className="range-value">{ranges.temperature.min}°C - {ranges.temperature.max}°C</span>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SoilDashboard
